import React, { useState, useEffect } from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'
import { supabase } from './lib/supabase'
import Login from './components/auth/Login'
import SignUp from './components/auth/SignUp'
import ForgotPassword from './components/auth/ForgotPassword'
import AppShell from './components/layout/AppShell'
import Onboarding from './components/Onboarding'
import Dashboard from './pages/Dashboard'
import Receipts from './pages/Receipts'
import Mileage from './pages/Mileage'
import Settings from './pages/Settings'
import Reports from './pages/Reports'
import Help from './pages/Help'
import Landing from './pages/Landing'
import Privacy from './pages/Privacy'
import Terms from './pages/Terms'
import Refunds from './pages/Refunds'
import Contact from './pages/Contact'
import './App.css'

// Wrapper that can read location inside Router context
function SignUpRoute({ session }) {
  // Log is solo product — no partner invites. Plain signup logic only.
  if (!session) return <SignUp />
  return <Navigate to="/" />
}

function App() {
  const [session, setSession] = useState(null)
  const [loading, setLoading] = useState(true)
  const [onboardingCompleted, setOnboardingCompleted] = useState(true)
  const [onboardingStep, setOnboardingStep] = useState(0)

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session)
      if (session) checkOnboarding(session.user.id)
      else setLoading(false)
    })
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session)
      if (session) checkOnboarding(session.user.id)
    })
    return () => subscription.unsubscribe()
  }, [])

  const checkOnboarding = async (userId) => {
    // Read onboarding state from profile row. New users get onboarding_completed = false
    // (the default), so they flow through the 3-step onboarding. Existing users who
    // already completed keep their completed state.
    try {
      const { data: profile, error } = await supabase
        .from('profiles')
        .select('onboarding_completed, onboarding_step')
        .eq('id', userId)
        .single()
      if (error) throw error
      const completed = !!profile?.onboarding_completed
      setOnboardingCompleted(completed)
      setOnboardingStep(profile?.onboarding_step || 0)
    } catch (err) {
      // Fail open: if we can't read onboarding state, assume completed so the user
      // isn't stuck. They can still manually resume via the Dashboard getting-started card.
      console.error('Failed to load onboarding state:', err)
      setOnboardingCompleted(true)
    } finally {
      setLoading(false)
    }
  }

  const handleOnboardingComplete = async () => {
    setOnboardingCompleted(true)
  }

  if (loading) return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh',
      background: '#FAF7F2',
      fontFamily: "'Fraunces', serif"
    }}>
      <div style={{ color: '#1E1040', fontSize: '24px', fontWeight: '900' }}>
        Merq<span style={{ color: '#0D9488', fontStyle: 'italic' }}>Log</span>
      </div>
    </div>
  )

  return (
    <Router>
      {session && !onboardingCompleted && (
        <Onboarding
          onComplete={handleOnboardingComplete}
          initialStep={onboardingStep}
        />
      )}
      <Routes>
        <Route path="/landing" element={<Landing session={session} />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/refunds" element={<Refunds />} />
        <Route path="/contact" element={<Contact />} />

        <Route path="/" element={
          session
            ? <AppShell />
            : <Landing session={null} />
        }>
          <Route index element={
            session
              ? <Dashboard onboardingCompleted={onboardingCompleted} onResumeOnboarding={() => setOnboardingCompleted(false)} />
              : null
          } />
          <Route path="receipts" element={<Receipts />} />
          <Route path="mileage" element={<Mileage />} />
          <Route path="reports" element={<Reports />} />
          <Route path="settings" element={<Settings />} />
          <Route path="help" element={<Help />} />
        </Route>

        <Route path="/login" element={!session ? <Login /> : <Navigate to="/" />} />
        <Route path="/signup" element={<SignUpRoute session={session} />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
      </Routes>
    </Router>
  )
}

export default App
